import axios from 'axios';
import type {
  RoleListItem,
  RoleDetailResponse,
  CreateRoleRequest,
  UpdateRoleRequest,
  UpdateRolePermissionsRequest,
  PageResponse,
  RoleSearchCondition,
  PermissionItem,
} from '../types/role';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const roleApi = {
  /** GET /api/eraf/roles — 역할 목록 (페이징/검색/필터/정렬) */
  list: (params: RoleSearchCondition): Promise<PageResponse<RoleListItem>> => {
    const searchParams = new URLSearchParams();
    if (params.keyword) searchParams.set('keyword', params.keyword);
    if (params.systemRole !== undefined) searchParams.set('systemRole', String(params.systemRole));
    if (params.page !== undefined) searchParams.set('page', String(params.page));
    if (params.size !== undefined) searchParams.set('size', String(params.size));
    if (params.sort) searchParams.set('sort', params.sort);

    return apiClient
      .get<unknown, { data: PageResponse<RoleListItem> }>(`/roles?${searchParams.toString()}`)
      .then((res) => res.data);
  },

  /** GET /api/eraf/roles/{id} — 역할 상세 */
  get: (id: number): Promise<RoleDetailResponse> =>
    apiClient
      .get<unknown, { data: RoleDetailResponse }>(`/roles/${id}`)
      .then((res) => res.data),

  /** POST /api/eraf/roles — 신규 역할 생성 */
  create: (body: CreateRoleRequest): Promise<RoleDetailResponse> =>
    apiClient
      .post<unknown, { data: RoleDetailResponse }>('/roles', body)
      .then((res) => res.data),

  /** PATCH /api/eraf/roles/{id} — 역할 부분 갱신 */
  update: (id: number, body: UpdateRoleRequest): Promise<RoleDetailResponse> =>
    apiClient
      .patch<unknown, { data: RoleDetailResponse }>(`/roles/${id}`, body)
      .then((res) => res.data),

  /** DELETE /api/eraf/roles/{id} — 역할 소프트 삭제 */
  delete: (id: number): Promise<void> =>
    apiClient.delete(`/roles/${id}`).then(() => undefined),

  /** PATCH /api/eraf/roles/{id}/permissions — 매트릭스 전체 교체 */
  updatePermissions: (id: number, body: UpdateRolePermissionsRequest): Promise<RoleDetailResponse> =>
    apiClient
      .patch<unknown, { data: RoleDetailResponse }>(`/roles/${id}/permissions`, body)
      .then((res) => res.data),

  /** GET /api/eraf/permissions — Permission 카탈로그 (read-only) */
  permissionCatalog: (params?: { resource?: string; action?: string }): Promise<PermissionItem[]> => {
    const searchParams = new URLSearchParams();
    if (params?.resource) searchParams.set('resource', params.resource);
    if (params?.action) searchParams.set('action', params.action);
    const qs = searchParams.toString();
    return apiClient
      .get<unknown, { data: PermissionItem[] }>(`/permissions${qs ? `?${qs}` : ''}`)
      .then((res) => res.data);
  },
};
